import { create } from 'zustand';
import i18n from 'i18n';
import { AuthApi, RestApiError, StatusCodeEnum, type ConfirmLoginRequest, type ConfirmLoginResponse } from '@shared/api';
import type { BaseReturn } from '@shared/types';
import { AuthUtils } from '@shared/utils';

interface UseConfirmLoginStore {
  loading: boolean;
  data: ConfirmLoginResponse | null;
  error?: string | null;
  confirmLogin: (request: ConfirmLoginRequest) => Promise<BaseReturn<ConfirmLoginResponse | null>>;
  clear: () => void;
}

export const useConfirmLoginStore = create<UseConfirmLoginStore>((set) => ({
  loading: false,
  data: null,
  error: null,

  confirmLogin: async (request) => {
    try {
      set({ loading: true, data: null, error: null });
      const result = await AuthApi.confirmLogin(request);
      AuthUtils.login(result);
      set({ loading: false, data: result ?? null });
      return { loading: false, data: result ?? null };
    } catch (e) {
      const restError = e as RestApiError;

      let errorMessage = 'Something went wrong, try again';
      switch (restError?.status) {
        case StatusCodeEnum.API_ERROR_UNAUTHORIZED:
          errorMessage = typeof restError.data === 'string' ? restError.data : 'Invalid code or password';
          break;
        case StatusCodeEnum.API_ERROR_FORBIDDEN:
          errorMessage =
            restError.data && restError.data[0]
              ? (restError.data[0] as string)
              : 'Incorrect password the user was locked out of the system for 15 mins, Please contact support if you need assistance';
          break;
      }

      const error = i18n.t(`errors.api.${errorMessage}`, errorMessage);
      set({ loading: false, data: null, error });
      return { loading: false, data: null, error };
    }
  },

  clear: () => set({ loading: false, data: null, error: null }),
}));
